import { ActionRequest } from '../types'
import { LoadingButton, LoadingButtonProps } from '@mui/lab'
import { updateRequestState } from '../services/requestService'
import { RequestableType } from '../enums/RequestableType'
import { useDispatch } from 'react-redux'
import { createSuccessNotification, createWarningNotification } from '../redux/slices/notifications'
import { deleteCompanyRequest, deleteProjectRequest } from '../redux/slices/requests'
import { createUserNotification } from '../utils/createUserNotification'
import { RequestAccessErrors } from '../enums/RequestAccessErrors'
import { useAuth } from '../hooks'
import { useTranslation } from 'react-i18next'
import { RequestStatus } from '../enums/RequestStatus'
import { useState } from 'react'
import UpdateActionRequestStateInput from '../types/UpdateActionRequestState'

export interface ActionRequestAcceptButtonProps
  extends Omit<LoadingButtonProps, 'children' | 'onClick' | 'loading'> {
  actionRequest: ActionRequest
  label?: string
  submitInput?: Omit<UpdateActionRequestStateInput, 'status' | 'approver' | 'newEndDate'>
  afterSubmit?: (actionRequest: ActionRequest) => void
}

export default function ActionRequestAcceptButton({
  actionRequest,
  label,
  afterSubmit,
  submitInput,
  ...props
}: ActionRequestAcceptButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The approver.
   */
  const approver = `${currentUser?.firstName} ${currentUser?.lastName}`

  /**
   * Indicates if the action request is being updated.
   */
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  /**
   * Handle the "accept" action.
   */
  async function handleAccept() {
    try {
      setIsSubmitting(true)

      const updatedRequest = await updateRequestState(actionRequest.id, {
        approver,
        status: RequestStatus.APPROVED,
        ...submitInput
      })

      switch (updatedRequest.requestableType) {
        case RequestableType.COMPANY:
          dispatch(deleteCompanyRequest(updatedRequest.id))
          break
        case RequestableType.PROJECT:
          dispatch(deleteProjectRequest(updatedRequest.id))
          break
        default:
          break
      }

      dispatch(createSuccessNotification(t('requestApproveSuccess')))

      afterSubmit?.(updatedRequest)
    } catch (error) {
      const errorMessage = createUserNotification({
        user: currentUser,
        type: RequestAccessErrors.UPDATE,
        error: error
      })

      dispatch(createWarningNotification(t(errorMessage.key) + t(errorMessage.message)))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <LoadingButton
      variant="text"
      color="success"
      {...props}
      loading={isSubmitting}
      onClick={handleAccept}
    >
      {label ?? t('accept')}
    </LoadingButton>
  )
}
